import { RESPONSIVE_IMAGE_VARIANTS } from "../generated/responsive-image-variants";

/** Raster formats the image components can request variants in. */
export type ImageFormat = "webp" | "jpg" | "png";

/** Lookup shape of the generated manifest — path incl. format → available widths. */
export type VariantManifest = Readonly<Record<string, readonly number[]>>;

/**
 * Split a path into base and extension, ignoring dots that belong to a
 * directory (`/images/v1.2/logo` has no extension).
 */
const splitExtension = (src: string): { basePath: string; extension: string } => {
    const lastSlash = src.lastIndexOf("/");
    const lastDot = src.lastIndexOf(".");

    if (lastDot <= lastSlash + 1) {
        return { basePath: src, extension: "" };
    }

    return { basePath: src.slice(0, lastDot), extension: src.slice(lastDot + 1) };
};

/**
 * Derive the variant format from a path's extension.
 *
 * `.jpeg` maps to `jpg`, because `generate-responsive-images.ts` writes sharp's
 * jpeg output as `-<w>w.jpg` — the only `.jpeg` source in the repo has `.jpg`
 * variants. Vector and unsupported formats return `undefined`: they have no
 * raster variants, and the caller must then use the original.
 */
export const toImageFormat = (src: string): ImageFormat | undefined => {
    const { extension } = splitExtension(src);

    switch (extension.toLowerCase()) {
        case "webp":
            return "webp";
        case "jpg":
        case "jpeg":
            return "jpg";
        case "png":
            return "png";
        default:
            return undefined;
    }
};

/**
 * Build a `srcset` that lists **only variants that exist on disk**.
 *
 * ## Warum diese Funktion existiert (11.8.2026)
 *
 * `ResponsiveImage` und `PictureImage` haben ihre Kandidaten blind konstruiert —
 * fuer jede angefragte Breite ein `<base>-<w>w.<ext>`. `generate-responsive-images.ts`
 * erzeugt aber bewusst keine Vergroesserungen, sondern nur Breiten
 * `<= originalWidth`. Beide Haelften waren einzeln richtig und zusammen falsch:
 * auf `/referenzen` lieferten **66 von 109** Kandidaten 404.
 *
 * Das ist deshalb schlimmer als ein fehlendes Bild: **`srcset` hat Vorrang vor
 * `src`, und ein 404 dort faellt nicht auf das Original zurueck.** Der Browser
 * zeigt Alt-Text. Ein 55x57 px grosses Logo wie `gulenko-logo.webp` hat gar
 * keine Varianten und war damit garantiert leer.
 *
 * Ein leerer Rueckgabewert ist deshalb ein **gueltiges und sicheres** Ergebnis:
 * ohne `srcset` nimmt der Browser `src`, und das existiert immer.
 *
 * @param src Bildpfad wie im Content/Component notiert (mit Endung)
 * @param widths vom Aufrufer gewuenschte Breiten — es werden nie mehr geliefert
 * @param format angefragtes Format; die Varianten unterscheiden sich je Format
 * @param variants Manifest-Override fuer Tests; sonst das generierte Manifest
 * @returns `srcset`-Wert, oder `""` wenn keine der Breiten wirklich existiert
 */
export const buildSrcSet = (
    src: string,
    widths: readonly number[],
    format: ImageFormat,
    variants: VariantManifest = RESPONSIVE_IMAGE_VARIANTS
): string => {
    const { basePath } = splitExtension(src);
    const available = variants[`${basePath}.${format}`] ?? [];

    if (available.length === 0) {
        return "";
    }

    const usable = widths.filter(width => available.includes(width));

    if (usable.length === 0) {
        return "";
    }

    return usable.map(width => `${basePath}-${width}w.${format} ${width}w`).join(", ");
};
